// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IProductFilter, IProductFilterType } from '@/models/products.model';

// Components
import { StyledFiltersWrapper, StyledFormItem, StyledSelect } from './ProductFiltersBlockStyles';
import { SliderFilterSelect } from '..';

// Helpers
import { getFilterOptions, getFilterDefaultValue } from '../../helpers/productCharacteristicsHelpers';
import { useCharacteristicsValidationRules } from '../../hooks';
import { getSortedProductFilters } from '../../helpers';

interface ProductFiltersBlockProps {
  filters: IProductFilter[];
}

const ProductFiltersBlock = ({ filters }: ProductFiltersBlockProps) => {
  const { t } = useTranslation('products');
  const { requiredRule, getSliderValidationRule } = useCharacteristicsValidationRules();
  const sortedFilters = filters && getSortedProductFilters(filters);

  const renderFilterInput = (filter: IProductFilter) => {
    if (filter.type === IProductFilterType.SLIDER) {
      const validationRules = [];

      if (filter.required) {
        validationRules.push(requiredRule);
      }

      if (filter.min && filter.max) {
        validationRules.push(getSliderValidationRule(filter));
      }

      return (
        <StyledFormItem key={filter.id} name={filter.id} label={filter.title} rules={validationRules}>
          <SliderFilterSelect filter={filter} options={getFilterOptions(filter)} />
        </StyledFormItem>
      );
    }

    return (
      <StyledFormItem
        key={filter.id}
        name={filter.id}
        label={filter.title}
        rules={filter.required ? [requiredRule] : undefined}
      >
        <StyledSelect
          allowClear
          placeholder={t('characteristics_filters_placeholder')}
          options={getFilterOptions(filter)}
          defaultValue={getFilterDefaultValue(filter)}
          showSearch
          isMultiselect={
            filter.type === IProductFilterType.MULTISELECT_WITH_SEARCH || filter.type === IProductFilterType.MULTISELECT
          }
          maxTagCount={3}
        />
      </StyledFormItem>
    );
  };

  const mappedProperties = useMemo(() => {
    return sortedFilters?.map((item) => <>{renderFilterInput(item)}</>);
  }, [filters]);

  return <StyledFiltersWrapper>{mappedProperties}</StyledFiltersWrapper>;
};

export default ProductFiltersBlock;
